/**
 * Anlagen.js — Hauptpage `/anlagen`
 *
 * Feb 2026 — AfA-Modul gemäß /app/memory/KONZEPT_AFA_2026.md
 * Pro-gated (Frage 7A). Free/Basic sehen UpgradeRequiredView.
 *
 * Struktur:
 *  - Header mit „Neue Anlage" Button
 *  - KPI-Bento (4 Cards)
 *  - Filter-Pills (Status)
 *  - Tabelle der Anlagen
 *  - Empty-State mit Edukations-Card
 *  - Detail-Sheet beim Klick auf Zeile
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Boxes, Plus, Filter, Search, Lock, ArrowRightLeft, Calendar, FileText, Loader2, Sparkles,
} from "lucide-react";
import api from "@/utils/api";
import { useIsPro } from "@/hooks/useIsPro";
import UpgradeRequiredView from "@/components/UpgradeRequiredView";
import AssetDetailSheet from "@/components/anlagen/AssetDetailSheet";
import AssetCreateWizard from "@/components/anlagen/AssetCreateWizard";
import AssetDisposalWizard from "@/components/anlagen/AssetDisposalWizard";
import ReceiptUpload from "@/components/ReceiptUpload";
import { useUserCountry } from "@/hooks/useUserCountry";

const STATUS_FILTERS = [
  { key: "all", label: "Alle aktiven" },
  { key: "active", label: "Aktiv" },
  { key: "disposed", label: "Veräußert" },
];

export default function Anlagen() {
  const isPro = useIsPro();
  const { country } = useUserCountry();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [disposeAsset, setDisposeAsset] = useState(null);
  const [receiptUploadOpen, setReceiptUploadOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const res = await api.get("/fixed-assets");
      setAssets(res.data || []);
    } catch {
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPro) loadAssets();
  }, [isPro]);

  // Deep-Link: `/anlagen?asset=<id>` öffnet direkt das Detail-Sheet (Feb 2026)
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isPro) return;
    const params = new URLSearchParams(location.search);
    const aid = params.get("asset");
    if (aid) {
      setSelectedId(aid);
      // URL-Parameter wieder entfernen, damit Reload nicht erneut öffnet
      navigate("/anlagen", { replace: true });
    }
  }, [location.search, isPro, navigate]);

  // KPIs
  const kpi = useMemo(() => {
    const active = assets.filter((a) => a.status === "active");
    const totalCost = active.reduce((s, a) => s + (a.cost_net || 0), 0);
    const currentYear = new Date().getFullYear();
    // Buchwert ist im Asset nicht direkt drin — schätzen aus cost_net minus (years_elapsed × annual)
    // Für genaue Werte müsste man postings laden. Hier nur grobe Schätzung im KPI.
    const totalBookValue = active.reduce((s, a) => {
      const annual = (a.cost_net || 0) / (a.useful_life_years || 1);
      const startYear = parseInt((a.depreciation_start_date || "2026")[0]) > 0
        ? parseInt(a.depreciation_start_date.slice(0, 4))
        : currentYear;
      const elapsed = Math.max(0, currentYear - startYear);
      return s + Math.max(0, (a.cost_net || 0) - annual * elapsed);
    }, 0);
    const yearlyAfa = active.reduce((s, a) => s + (a.cost_net || 0) / (a.useful_life_years || 1), 0);
    return {
      count: active.length,
      totalCost,
      totalBookValue,
      yearlyAfa,
    };
  }, [assets]);

  const filtered = useMemo(() => {
    let f = assets;
    if (filter === "active") f = f.filter((a) => a.status === "active");
    if (filter === "disposed") f = f.filter((a) => a.status === "disposed");
    if (search) {
      const s = search.toLowerCase();
      f = f.filter((a) => (a.name || "").toLowerCase().includes(s) || (a.vendor || "").toLowerCase().includes(s));
    }
    return f;
  }, [assets, filter, search]);

  // ── Pro-Gating ────────────────────────────────────────────────────────
  if (!isPro) {
    return (
      <UpgradeRequiredView
        source="anlagevermoegen"
        title="Anlagevermögen & AfA-Planung"
        subtitle="Schreibe Wirtschaftsgüter rechtssicher über mehrere Jahre ab — DACH-konform."
        icon={Boxes}
        features={[
          "DACH-AfA-Tabelle mit 49 Wirtschaftsgut-Kategorien (DE, AT, CH)",
          "Automatische AfA-Berechnung: linear, degressiv, Sammelposten, GWG",
          "DE Pro-rata-temporis · AT Halbjahres-Regel · CH Degressiv vom Buchwert",
          "Veräußerung / Entnahme mit Gewinn/Verlust-Berechnung",
          "§ 15a UStG-Vorsteuerberichtigung bei Privat-Anteil-Änderung",
          "Anlagenspiegel im DATEV/BMD-Export + PDF",
          "Steuerberater sieht alles + kann sperren (Lock)",
        ]}
      />
    );
  }

  // ── Receipt-Drop-Handler: prefill öffnen ──
  const handleReceiptDataExtracted = useCallback((data) => {
    const defaultVat = country === "AT" ? 20 : country === "CH" ? 8.1 : 19;
    setCreatePrefill({
      name: data.description || data.vendor || "Anlage aus Beleg",
      vendor: data.vendor || "",
      date: data.date || new Date().toISOString().slice(0, 10),
      cost_gross: parseFloat(data.amount) || 0,
      vat_rate: data.vat_rate ?? defaultVat,
      suggested_category: data.suggested_asset_category || null,
      receipt: data._receiptStorage
        ? {
            file_key: data._receiptStorage.file_key,
            filename: data._receiptStorage.filename,
            storage_uri: data._receiptStorage.storage_uri,
            content_type: data._receiptStorage.content_type,
          }
        : null,
    });
    setReceiptUploadOpen(false);
    setCreateOpen(true);
  }, [country]);

  // ── Loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500" data-testid="anlagen-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Anlagen…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="anlagen-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2 font-manrope" data-testid="anlagen-title">
            <Boxes className="h-7 w-7 text-emerald-600" />
            Anlagevermögen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Verwalte deine Wirtschaftsgüter und AfA-Planung — {country}-konform.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const year = new Date().getFullYear();
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/fixed-assets/anlagenspiegel/${year}.pdf`;
              const token = localStorage.getItem("auth_token") || "";
              // Server erwartet Bearer-Token im Header — daher via fetch + Blob-Download
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.ok ? r.blob() : Promise.reject(r.statusText))
                .then((blob) => {
                  const u = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u;
                  a.download = `Anlagenspiegel_${year}.pdf`;
                  a.click();
                  URL.revokeObjectURL(u);
                })
                .catch((err) => console.error("PDF-Download fehlgeschlagen", err));
            }}
            data-testid="anlagenspiegel-download-btn"
          >
            <FileText className="h-4 w-4 mr-1.5" /> Anlagenspiegel {new Date().getFullYear()}
          </Button>
          <Button
            variant="outline"
            onClick={() => setReceiptUploadOpen(true)}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="upload-receipt-btn"
          >
            <FileText className="h-4 w-4 mr-1.5" /> Per Beleg-Upload
          </Button>
          <Button onClick={() => { setCreatePrefill(null); setCreateOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-asset-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Neue Anlage
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {assets.length === 0 ? (
        <Card className="border-emerald-200/50">
          <CardContent className="py-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 mb-3">
              <Sparkles className="h-6 w-6" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Noch keine Anlagen erfasst</h2>
            <p className="text-sm text-slate-600 max-w-lg mx-auto mb-5">
              Wirtschaftsgüter (Laptop, Pkw, Möbel, Software) über der GWG-Grenze ({country === "AT" ? "1.000 €" : country === "CH" ? "1.000 CHF" : "800 €"} netto)
              müssen über ihre Nutzungsdauer abgeschrieben werden. <strong>Lade einfach den Beleg hoch</strong> —
              die KI liest alle Daten aus, oder erfasse die Anlage manuell.
            </p>
            <div className="flex gap-2 justify-center">
              <Button
                onClick={() => setReceiptUploadOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="empty-upload-receipt-btn"
              >
                <FileText className="h-4 w-4 mr-1.5" /> Beleg hochladen (Drag & Drop)
              </Button>
              <Button variant="outline" onClick={() => { setCreatePrefill(null); setCreateOpen(true); }} data-testid="empty-add-asset-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Manuell erfassen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Bento */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <KpiCard label="Aktive Anlagen" value={kpi.count} testid="kpi-count" />
            <KpiCard label="Buchwert gesamt" value={`${kpi.totalBookValue.toFixed(0)} €`} testid="kpi-bookvalue" highlight />
            <KpiCard label="Jahres-AfA" value={`${kpi.yearlyAfa.toFixed(0)} €`} testid="kpi-yearly-afa" />
            <KpiCard label="Anschaffungswert" value={`${kpi.totalCost.toFixed(0)} €`} testid="kpi-totalcost" />
          </div>

          {/* Filter + Search */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  filter === f.key
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                    : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
            <div className="relative ml-auto">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input
                placeholder="Suchen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-56"
                data-testid="anlagen-search"
              />
            </div>
          </div>

          {/* Tabelle */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Bezeichnung</th>
                    <th className="px-4 py-3 text-left">Anschaffung</th>
                    <th className="px-4 py-3 text-right">AK netto</th>
                    <th className="px-4 py-3 text-right">Jahres-AfA</th>
                    <th className="px-4 py-3 text-left">Methode</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-10 text-slate-400">Keine Treffer</td></tr>
                  ) : filtered.map((a) => {
                    const annual = (a.cost_net || 0) / (a.useful_life_years || 1);
                    return (
                      <tr
                        key={a.id}
                        onClick={() => setSelectedId(a.id)}
                        className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer"
                        data-testid={`asset-row-${a.id}`}
                      >
                        <td className="px-4 py-2.5 font-medium text-slate-800">
                          <div className="flex items-center gap-2">
                            {a.name}
                            {a.locked_by_advisor && <Lock className="h-3 w-3 text-rose-500" />}
                            {a.private_use_percentage > 0 && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                                {a.private_use_percentage}% privat
                              </Badge>
                            )}
                          </div>
                          {a.vendor && <div className="text-xs text-slate-500">{a.vendor}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600 text-xs">
                          <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{a.acquisition_date}</div>
                          <div className="text-slate-400">{a.useful_life_years} J</div>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.cost_net?.toFixed(2)} €</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{annual.toFixed(2)} €</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className="text-xs">
                            {a.depreciation_method === "linear" ? "Linear"
                              : a.depreciation_method === "degressive" ? "Degressiv"
                              : a.depreciation_method === "pool_5y" ? "Pool 5J"
                              : "GWG"}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          {a.status === "active" ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Aktiv</Badge>
                          ) : a.status === "disposed" ? (
                            <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200">
                              <ArrowRightLeft className="h-3 w-3 mr-1" />Veräußert
                            </Badge>
                          ) : (
                            <Badge variant="outline">Abgeschr.</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Detail Sheet */}
      <AssetDetailSheet
        assetId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={loadAssets}
        onDispose={(asset) => { setSelectedId(null); setDisposeAsset(asset); }}
      />

      {/* Create Wizard */}
      <AssetCreateWizard
        open={createOpen}
        onClose={() => { setCreateOpen(false); setCreatePrefill(null); }}
        country={country || "DE"}
        prefill={createPrefill}
        onCreated={loadAssets}
      />

      {/* Receipt Upload — gleicher Drag&Drop-Flow wie unter /finanzen */}
      <ReceiptUpload
        isOpen={receiptUploadOpen}
        onClose={() => setReceiptUploadOpen(false)}
        onDataExtracted={handleReceiptDataExtracted}
      />

      {/* Disposal Wizard */}
      <AssetDisposalWizard
        open={!!disposeAsset}
        onClose={() => setDisposeAsset(null)}
        asset={disposeAsset}
        onDisposed={loadAssets}
      />
    </div>
  );
}

function KpiCard({ label, value, testid, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${highlight ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}
      data-testid={testid}
    >
      <div className={`text-xs ${highlight ? "text-emerald-700" : "text-slate-500"} mb-1`}>{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
